import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { birthdayGiftService } from '@/services/birthdayGift.service';
import type {
  BirthdayGiftCoordination,
  BirthdayGiftSuggestion,
  CreateSuggestionDto,
  MarkPurchasedDto,
  GiftCoordinationFilters,
} from '@/types/birthdayGift.types';
import { ApiError } from '@/services/api';

// Birthday Gift state interface
interface BirthdayGiftState {
  coordination: BirthdayGiftCoordination | null;
  upcomingCoordinations: BirthdayGiftCoordination[];
  isLoading: boolean;
  isLoadingUpcoming: boolean;
  isSaving: boolean;
  isVoting: boolean;
  isClaiming: boolean;
  isPurchasing: boolean;
  error: string | null;
}

// Initial state
const initialState: BirthdayGiftState = {
  coordination: null,
  upcomingCoordinations: [],
  isLoading: false,
  isLoadingUpcoming: false,
  isSaving: false,
  isVoting: false,
  isClaiming: false,
  isPurchasing: false,
  error: null,
};

// Async thunks

// Fetch coordination for a birthday
export const fetchGiftCoordination = createAsyncThunk<
  BirthdayGiftCoordination,
  string,
  { rejectValue: string }
>('birthdayGifts/fetchCoordination', async (birthdayId, { rejectWithValue }) => {
  try {
    return await birthdayGiftService.getCoordination(birthdayId);
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.statusCode === 403) {
        return rejectWithValue('Du kannst die Geschenk-Koordination für deinen eigenen Geburtstag nicht sehen');
      }
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Geschenk-Koordination');
  }
});

// Fetch upcoming coordinations
export const fetchUpcomingCoordinations = createAsyncThunk<
  BirthdayGiftCoordination[],
  number | undefined,
  { rejectValue: string }
>('birthdayGifts/fetchUpcoming', async (days = 60, { rejectWithValue }) => {
  try {
    return await birthdayGiftService.getUpcomingCoordinations(days);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Geschenk-Koordinationen');
  }
});

// Create a suggestion
export const createSuggestion = createAsyncThunk<
  BirthdayGiftSuggestion,
  { birthdayId: string; data: CreateSuggestionDto },
  { rejectValue: string }
>('birthdayGifts/createSuggestion', async ({ birthdayId, data }, { rejectWithValue }) => {
  try {
    return await birthdayGiftService.createSuggestion(birthdayId, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Erstellen des Vorschlags');
  }
});

// Delete a suggestion
export const deleteSuggestion = createAsyncThunk<
  string,
  { birthdayId: string; suggestionId: string },
  { rejectValue: string }
>('birthdayGifts/deleteSuggestion', async ({ birthdayId, suggestionId }, { rejectWithValue }) => {
  try {
    await birthdayGiftService.deleteSuggestion(birthdayId, suggestionId);
    return suggestionId;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Löschen des Vorschlags');
  }
});

// Vote on a suggestion
export const voteOnSuggestion = createAsyncThunk<
  BirthdayGiftSuggestion,
  { birthdayId: string; suggestionId: string },
  { rejectValue: string }
>('birthdayGifts/vote', async ({ birthdayId, suggestionId }, { rejectWithValue }) => {
  try {
    return await birthdayGiftService.vote(birthdayId, suggestionId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Abstimmen');
  }
});

// Remove vote from a suggestion
export const removeVoteFromSuggestion = createAsyncThunk<
  BirthdayGiftSuggestion,
  { birthdayId: string; suggestionId: string },
  { rejectValue: string }
>('birthdayGifts/removeVote', async ({ birthdayId, suggestionId }, { rejectWithValue }) => {
  try {
    return await birthdayGiftService.removeVote(birthdayId, suggestionId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Entfernen der Stimme');
  }
});

// Claim the gift
export const claimGift = createAsyncThunk<
  BirthdayGiftCoordination,
  string,
  { rejectValue: string }
>('birthdayGifts/claim', async (birthdayId, { rejectWithValue }) => {
  try {
    return await birthdayGiftService.claimGift(birthdayId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Beanspruchen des Geschenks');
  }
});

// Unclaim the gift
export const unclaimGift = createAsyncThunk<
  BirthdayGiftCoordination,
  string,
  { rejectValue: string }
>('birthdayGifts/unclaim', async (birthdayId, { rejectWithValue }) => {
  try {
    return await birthdayGiftService.unclaimGift(birthdayId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Zurücknehmen der Beanspruchung');
  }
});

// Mark as purchased
export const markGiftPurchased = createAsyncThunk<
  BirthdayGiftCoordination,
  { birthdayId: string; data: MarkPurchasedDto },
  { rejectValue: string }
>('birthdayGifts/purchase', async ({ birthdayId, data }, { rejectWithValue }) => {
  try {
    return await birthdayGiftService.markPurchased(birthdayId, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Markieren als gekauft');
  }
});

// Birthday Gift slice
const birthdayGiftSlice = createSlice({
  name: 'birthdayGifts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCoordination: (state) => {
      state.coordination = null;
    },
    resetBirthdayGifts: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch coordination
    builder
      .addCase(fetchGiftCoordination.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGiftCoordination.fulfilled, (state, action) => {
        state.isLoading = false;
        state.coordination = action.payload;
      })
      .addCase(fetchGiftCoordination.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch upcoming
    builder
      .addCase(fetchUpcomingCoordinations.pending, (state) => {
        state.isLoadingUpcoming = true;
        state.error = null;
      })
      .addCase(fetchUpcomingCoordinations.fulfilled, (state, action) => {
        state.isLoadingUpcoming = false;
        state.upcomingCoordinations = action.payload;
      })
      .addCase(fetchUpcomingCoordinations.rejected, (state, action) => {
        state.isLoadingUpcoming = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Create suggestion
    builder
      .addCase(createSuggestion.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createSuggestion.fulfilled, (state, action) => {
        state.isSaving = false;
        if (state.coordination) {
          state.coordination.suggestions.push(action.payload);
          state.coordination._count.suggestions++;
        }
      })
      .addCase(createSuggestion.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload ?? 'Fehler beim Erstellen';
      });

    // Delete suggestion
    builder
      .addCase(deleteSuggestion.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deleteSuggestion.fulfilled, (state, action) => {
        state.isSaving = false;
        if (state.coordination) {
          state.coordination.suggestions = state.coordination.suggestions.filter(
            (s) => s.id !== action.payload
          );
          state.coordination._count.suggestions--;
        }
      })
      .addCase(deleteSuggestion.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload ?? 'Fehler beim Löschen';
      });

    // Vote
    builder
      .addCase(voteOnSuggestion.pending, (state) => {
        state.isVoting = true;
        state.error = null;
      })
      .addCase(voteOnSuggestion.fulfilled, (state, action) => {
        state.isVoting = false;
        if (state.coordination) {
          const index = state.coordination.suggestions.findIndex(
            (s) => s.id === action.payload.id
          );
          if (index !== -1) {
            state.coordination.suggestions[index] = action.payload;
          }
        }
      })
      .addCase(voteOnSuggestion.rejected, (state, action) => {
        state.isVoting = false;
        state.error = action.payload ?? 'Fehler beim Abstimmen';
      });

    // Remove vote
    builder
      .addCase(removeVoteFromSuggestion.pending, (state) => {
        state.isVoting = true;
        state.error = null;
      })
      .addCase(removeVoteFromSuggestion.fulfilled, (state, action) => {
        state.isVoting = false;
        if (state.coordination) {
          const index = state.coordination.suggestions.findIndex(
            (s) => s.id === action.payload.id
          );
          if (index !== -1) {
            state.coordination.suggestions[index] = action.payload;
          }
        }
      })
      .addCase(removeVoteFromSuggestion.rejected, (state, action) => {
        state.isVoting = false;
        state.error = action.payload ?? 'Fehler beim Entfernen der Stimme';
      });

    // Claim
    builder
      .addCase(claimGift.pending, (state) => {
        state.isClaiming = true;
        state.error = null;
      })
      .addCase(claimGift.fulfilled, (state, action) => {
        state.isClaiming = false;
        state.coordination = action.payload;
      })
      .addCase(claimGift.rejected, (state, action) => {
        state.isClaiming = false;
        state.error = action.payload ?? 'Fehler beim Beanspruchen';
      });

    // Unclaim
    builder
      .addCase(unclaimGift.pending, (state) => {
        state.isClaiming = true;
        state.error = null;
      })
      .addCase(unclaimGift.fulfilled, (state, action) => {
        state.isClaiming = false;
        state.coordination = action.payload;
      })
      .addCase(unclaimGift.rejected, (state, action) => {
        state.isClaiming = false;
        state.error = action.payload ?? 'Fehler beim Zurücknehmen';
      });

    // Purchase
    builder
      .addCase(markGiftPurchased.pending, (state) => {
        state.isPurchasing = true;
        state.error = null;
      })
      .addCase(markGiftPurchased.fulfilled, (state, action) => {
        state.isPurchasing = false;
        state.coordination = action.payload;
      })
      .addCase(markGiftPurchased.rejected, (state, action) => {
        state.isPurchasing = false;
        state.error = action.payload ?? 'Fehler beim Markieren als gekauft';
      });
  },
});

// Export actions
export const { clearError, clearCoordination, resetBirthdayGifts } = birthdayGiftSlice.actions;

// Selectors
export const selectGiftCoordination = (state: { birthdayGifts: BirthdayGiftState }) =>
  state.birthdayGifts.coordination;
export const selectUpcomingGiftCoordinations = (state: { birthdayGifts: BirthdayGiftState }) =>
  state.birthdayGifts.upcomingCoordinations;
export const selectGiftSuggestions = (state: { birthdayGifts: BirthdayGiftState }) =>
  state.birthdayGifts.coordination?.suggestions ?? [];
export const selectGiftLoading = (state: { birthdayGifts: BirthdayGiftState }) => ({
  isLoading: state.birthdayGifts.isLoading,
  isLoadingUpcoming: state.birthdayGifts.isLoadingUpcoming,
  isSaving: state.birthdayGifts.isSaving,
  isVoting: state.birthdayGifts.isVoting,
  isClaiming: state.birthdayGifts.isClaiming,
  isPurchasing: state.birthdayGifts.isPurchasing,
});
export const selectGiftError = (state: { birthdayGifts: BirthdayGiftState }) =>
  state.birthdayGifts.error;

// Export reducer
export default birthdayGiftSlice.reducer;
